import {FieldArray, FieldArrayRenderProps, useFormikContext} from 'formik';
import {
    ActionIcon,
    Alert,
    Button,
    Card,
    Group,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import {AiOutlineDelete, AiOutlinePlus} from 'react-icons/ai';

import {
    EMPTY_STAGE_VALUES,
    RegisterInfrastructureFormValues,
} from '../types-infrastructure';

// FNP-1016: the per-stage half of the inventory.
//
// ⚠️ THE ARRAY IS SPARSE, AND THAT IS THE FEATURE. An absent stage means "not
// provisioned". There are deliberately no fixed dev/test/prod rows to fill in:
// the `{dev,test,prod}` map this replaces forced two empty strings into live
// prod data purely to defeat a non-null rule, leaving "provisioned to nothing"
// and "never recorded" indistinguishable.
//
// So the admin ADDS the stages that actually exist. A legacy application
// serving only production gets exactly one row.

export const InfrastructureStagesField = () => {

    const {
        values,
        handleChange,
    } = useFormikContext<RegisterInfrastructureFormValues>();

    const rows = values.stages ?? [];

    return (
        <FieldArray name='stages'>
            {({
                  push,
                  remove,
              }: FieldArrayRenderProps) => (
                <Stack gap={'sm'}>
                    <div>
                        <Title order={5}>Stages</Title>
                        <Text size={'xs'} c={'dimmed'}>
                            Add one row per environment that actually exists. A stage you do
                            not add is recorded as <strong>not provisioned</strong> — which is
                            correct for an application that only ever ran in production. Do
                            not add a row you cannot fill in.
                        </Text>
                    </div>

                    {rows.length === 0 ? (
                        <Alert
                            color={'gray'}
                            variant={'light'}
                        >
                            No stages recorded yet. The shared repository and pipeline details
                            above can be saved on their own; add a stage when you know its
                            account, region and stack name.
                        </Alert>
                    ) : null}

                    {rows.map((row, index) => (
                        <Card
                            key={index}
                            withBorder
                            padding={'md'}
                        >
                            <Stack gap={'sm'}>
                                <Group justify={'space-between'}>
                                    <Text
                                        fw={600}
                                        size={'sm'}
                                    >
                                        {row.stage ? row.stage : `Stage ${index + 1}`}
                                    </Text>
                                    <ActionIcon
                                        variant={'subtle'}
                                        color={'red'}
                                        aria-label={`Remove stage ${index + 1}`}
                                        onClick={() => remove(index)}
                                    >
                                        <AiOutlineDelete/>
                                    </ActionIcon>
                                </Group>

                                {/*
                                    ⚠️ These four are REQUIRED together. A row missing any
                                    of them is dropped from the submitted array rather than
                                    sent partially — `ValidateDeprovisionable` hard-refuses
                                    a missing stack name, and a stage entry that merely
                                    LOOKS present passes the eye test while orphaning
                                    whatever it failed to name.
                                */}
                                <TextInput
                                    label={'Stage'}
                                    description={'dev, test, prod — the environment name'}
                                    placeholder={'prod'}
                                    name={`stages.${index}.stage`}
                                    value={row.stage}
                                    onChange={handleChange}
                                    withAsterisk
                                />
                                <TextInput
                                    label={'Account id'}
                                    description={'The AWS account this stage is deployed into'}
                                    placeholder={'123456789012'}
                                    name={`stages.${index}.accountId`}
                                    value={row.accountId}
                                    onChange={handleChange}
                                    withAsterisk
                                />
                                <TextInput
                                    label={'Region'}
                                    placeholder={'us-west-2'}
                                    name={`stages.${index}.region`}
                                    value={row.region}
                                    onChange={handleChange}
                                    withAsterisk
                                />
                                <TextInput
                                    label={'Infrastructure stack name'}
                                    description={
                                        'The CloudFormation stack that serves this stage. '
                                        + 'Required: a teardown will not guess a stack name, '
                                        + 'because a stack it cannot find is reported as '
                                        + 'already deleted.'
                                    }
                                    placeholder={'my-app-infra-prod'}
                                    name={`stages.${index}.infraStackName`}
                                    value={row.infraStackName}
                                    onChange={handleChange}
                                    withAsterisk
                                />

                                <TextInput
                                    label={'Deployment bucket name'}
                                    description={'Optional — not every application deploys to S3'}
                                    name={`stages.${index}.deploymentBucketName`}
                                    value={row.deploymentBucketName}
                                    onChange={handleChange}
                                />
                                <TextInput
                                    label={'Domain'}
                                    description={'Optional'}
                                    name={`stages.${index}.domain`}
                                    value={row.domain}
                                    onChange={handleChange}
                                />
                                <TextInput
                                    label={'Hosted zone id'}
                                    description={'Optional'}
                                    name={`stages.${index}.hostedZoneId`}
                                    value={row.hostedZoneId}
                                    onChange={handleChange}
                                />
                                <TextInput
                                    label={'Certificate ARN'}
                                    description={'Optional'}
                                    name={`stages.${index}.certificateArn`}
                                    value={row.certificateArn}
                                    onChange={handleChange}
                                />
                            </Stack>
                        </Card>
                    ))}

                    <Group>
                        <Button
                            variant={'light'}
                            leftSection={<AiOutlinePlus/>}
                            onClick={() => push({...EMPTY_STAGE_VALUES})}
                        >
                            Add stage
                        </Button>
                    </Group>
                </Stack>
            )}
        </FieldArray>
    );
};
